import { supabase } from './client'
import { memCache, TTL } from '@lib/cache'
import { analyticsService, getAmountBucket } from '@lib/analytics/posthog.service'

export interface Appointment {
  id: string
  patient_id: string
  provider_id: string
  service_type: string
  status: 'requested' | 'scheduled' | 'completed' | 'cancelled' | 'rejected'
  scheduled_date: string
  scheduled_time?: string | null
  location?: string | null
  notes?: string | null
  total_cost: number
  duration?: string | null
  number_of_days?: number | null
  number_of_patients?: number | null
  urgency_level?: 'normal' | 'high' | 'urgent' | string | null
  appointment_type?: string | null
  conversation_id?: string | null
  continuation_of_appointment_id?: string | null
  completed_at?: string | null
  created_at: string
}

export interface AppointmentMedicalInfo {
  currentSymptoms?: string | null
  medicalHistory?: string | null
  allergies?: string | null
  currentMedications?: string | null
  surgeryHistory?: string | null
  familyHistory?: string | null
  accidentRecord?: string | null
  bloodType?: string | null
  heightCm?: number | null
  weightKg?: number | null
}

export interface VisitNote {
  id: string
  appointmentId: string
  noteText: string
  providerSignature?: string | null
  createdAt?: string | null
}

export interface UpsertVisitNoteData {
  appointmentId: string
  providerId: string
  patientId: string
  noteText: string
  providerSignature: string
}

export interface VisitPrescription {
  id: string
  drugName: string
  dosageInstructions: string
  sortOrder: number
}

export interface VisitResult {
  id: string
  appointmentId: string
  providerId: string
  patientId: string
  diagnosisText: string
  providerSignature: string
  createdAt: string
  updatedAt: string
  prescriptions: VisitPrescription[]
}

export interface SaveVisitResultData extends UpsertVisitNoteData {
  diagnosisText: string
  prescriptions: Array<{
    drugName: string
    dosageInstructions: string
  }>
}

export interface CreateAppointmentData {
  providerId: string
  serviceType: 'Doctor Visit' | 'Nurse Care' | 'Home Care' | 'Specialized Care' | 'Chat Consultation' | 'Dermatology'
  duration: 'single' | 'daily'
  scheduledDate: string
  scheduledTime?: string
  location: string
  appointmentType?: 'home' | 'chat'
  notes?: string
  urgencyLevel?: 'normal' | 'high' | 'urgent'
  numberOfDays?: number
  numberOfPatients?: number
  totalCost: number
  continuationOfAppointmentId?: string
  serviceAddressId?: string
  serviceAddressLatitude?: number | null
  serviceAddressLongitude?: number | null
  locationVerificationMode?: 'device_pair' | 'destination_address'
  verificationMode?: 'device_pair' | 'destination_address'
  destinationLatitude?: number | null
  destinationLongitude?: number | null
  destinationAddressSnapshot?: string | null
  medicalInfo?: {
    currentSymptoms?: string
    medicalHistory?: string
    allergies?: string
    currentMedications?: string
    surgeryHistory?: string
    familyHistory?: string
    accidentRecord?: string
    bloodType?: string
    heightCm?: number
    weightKg?: number
  }
}

export interface AppointmentPageResult {
  appointments: Appointment[]
  hasMore: boolean
  nextOffset: number | null
}

export interface PatientAppointmentQueryOptions {
  status?: string
  serviceType?: string
  excludeServiceType?: string
}

// Cached patient row id — avoids 2 extra round-trips before every list query
async function getPatientId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return null

  const cacheKey = `patient_id:${user.id}`
  const cached = memCache.get<string>(cacheKey)
  if (cached) return cached

  const { data } = await supabase.from('patients').select('id').eq('auth_id', user.id).single()
  if (!data?.id) return null

  memCache.set(cacheKey, data.id, TTL.ACTOR_ID)
  return data.id
}

const collapseChatContinuations = (appointments: Appointment[]): Appointment[] => {
  const latestByConversation = new Map<string, Appointment>()
  const nonChat: Appointment[] = []

  for (const apt of appointments) {
    if (apt.service_type !== 'Chat Consultation') {
      nonChat.push(apt)
      continue
    }
    const key = apt.conversation_id || apt.id
    const existing = latestByConversation.get(key)
    if (!existing || (apt.created_at || '') > (existing.created_at || '')) {
      latestByConversation.set(key, apt)
    }
  }

  return [...nonChat, ...Array.from(latestByConversation.values())].sort((a, b) =>
    (b.created_at || '').localeCompare(a.created_at || '')
  )
}

export const appointmentService = {
  async getVisitResult(appointmentId: string): Promise<VisitResult | null> {
    try {
      const { data, error } = await supabase
        .from('visit_results')
        .select('id, appointment_id, provider_id, patient_id, diagnosis_text, provider_signature, created_at, updated_at')
        .eq('appointment_id', appointmentId)
        .maybeSingle()

      if (error) throw error
      if (!data) return null

      const { data: prescriptions, error: prescriptionsError } = await supabase
        .from('visit_prescriptions')
        .select('id, drug_name, dosage_instructions, sort_order')
        .eq('visit_result_id', data.id)
        .order('sort_order', { ascending: true })

      if (prescriptionsError) throw prescriptionsError

      return {
        appointmentId: data.appointment_id,
        providerId: data.provider_id,
        patientId: data.patient_id,
        diagnosisText: data.diagnosis_text,
        providerSignature: data.provider_signature,
        createdAt: data.created_at,
        updatedAt: data.updated_at,
        id: data.id,
        prescriptions: (prescriptions || []).map((prescription: any) => ({
          id: prescription.id,
          drugName: prescription.drug_name,
          dosageInstructions: prescription.dosage_instructions,
          sortOrder: prescription.sort_order,
        })),
      }
    } catch {
      return null
    }
  },

  async saveVisitResult(data: SaveVisitResultData): Promise<{ success: boolean; result?: VisitResult; error?: string }> {
    try {
      if (!data.noteText.trim()) return { success: false, error: 'Visit note is required.' }
      if (!data.diagnosisText.trim()) return { success: false, error: 'Diagnosis is required.' }
      if (!data.providerSignature.trim()) return { success: false, error: 'Provider signature is required.' }
      if (data.prescriptions.some((prescription) => !prescription.drugName.trim() || !prescription.dosageInstructions.trim())) {
        return { success: false, error: 'Each prescription requires a drug name and dosage instructions.' }
      }

      const { data: saved, error } = await supabase.rpc('save_visit_result', {
        p_appointment_id: data.appointmentId,
        p_provider_id: data.providerId,
        p_patient_id: data.patientId,
        p_note_text: data.noteText.trim(),
        p_diagnosis_text: data.diagnosisText.trim(),
        p_provider_signature: data.providerSignature.trim(),
        p_prescriptions: data.prescriptions.map((prescription) => ({
          drugName: prescription.drugName.trim(),
          dosageInstructions: prescription.dosageInstructions.trim(),
        })),
      })

      if (error) throw error
      return { success: true, result: saved as VisitResult }
    } catch (error: any) {
      return { success: false, error: error.message || 'Unable to save visit result.' }
    }
  },

  async getPatientAppointments(status?: string, serviceType?: string, excludeServiceType?: string): Promise<Appointment[]> {
    try {
      const patientId = await getPatientId()
      if (!patientId) return []

      let query = supabase
        .from('appointments')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })

      if (status) query = query.eq('status', status)
      if (serviceType) query = query.eq('service_type', serviceType)
  if (excludeServiceType) query = query.neq('service_type', excludeServiceType)

      const { data, error } = await query
      if (error) throw error
      return collapseChatContinuations(data || [])
    } catch {
      return []
    }
  },

  async getPatientAppointmentsPage(
    status?: string,
    limit = 8,
    offset = 0,
    serviceType?: string,
    excludeServiceType?: string,
  ): Promise<AppointmentPageResult> {
    try {
      const patientId = await getPatientId()
      if (!patientId) return { appointments: [], hasMore: false, nextOffset: null }

      let query = supabase
        .from('appointments')
        .select('*')
        .eq('patient_id', patientId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit)

      if (status) query = query.eq('status', status)
      if (serviceType) query = query.eq('service_type', serviceType)
  if (excludeServiceType) query = query.neq('service_type', excludeServiceType)

      const { data, error } = await query
      if (error) throw error

      const rows = data || []
      const hasMore = rows.length > limit
      const appointments = collapseChatContinuations(rows.slice(0, limit))

      return {
        appointments,
        hasMore,
        nextOffset: hasMore ? offset + limit : null,
      }
    } catch {
      return { appointments: [], hasMore: false, nextOffset: null }
    }
  },

  async getAppointmentById(id: string): Promise<Appointment | null> {
    try {
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('id', id)
        .single()
      if (error) throw error
      return data
    } catch {
      return null
    }
  },

  async getAppointmentMedicalInfo(appointmentId: string): Promise<AppointmentMedicalInfo | null> {
    try {
      const { data, error } = await supabase
        .from('appointment_medical_info')
        .select(
          'current_symptoms, medical_history, allergies, current_medications,' +
          'surgery_history, family_history, accident_record, blood_type, height_cm, weight_kg'
        )
        .eq('appointment_id', appointmentId)
        .maybeSingle()

      if (error) throw error
      if (!data) return null

      return {
        currentSymptoms:    (data as any).current_symptoms    ?? null,
        medicalHistory:     (data as any).medical_history     ?? null,
        allergies:          (data as any).allergies           ?? null,
        currentMedications: (data as any).current_medications ?? null,
        surgeryHistory:     (data as any).surgery_history     ?? null,
        familyHistory:      (data as any).family_history      ?? null,
        accidentRecord:     (data as any).accident_record     ?? null,
        bloodType:          (data as any).blood_type          ?? null,
        heightCm:           (data as any).height_cm           ?? null,
        weightKg:           (data as any).weight_kg           ?? null,
      }
    } catch {
      return null
    }
  },

  async getPatientEmr(): Promise<AppointmentMedicalInfo | null> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return null

      const { data: patient } = await supabase
        .from('patients')
        .select('id')
        .eq('auth_id', user.id)
        .single()
      if (!patient) return null

      const { data, error } = await supabase
        .from('patient_emr')
        .select(
          'current_symptoms, medical_history, allergies, current_medications,' +
          'surgery_history, family_history, accident_record, blood_type, height_cm, weight_kg'
        )
        .eq('patient_id', patient.id)
        .maybeSingle()

      if (error) throw error
      if (!data) return null

      return {
        currentSymptoms:    (data as any).current_symptoms    ?? null,
        medicalHistory:     (data as any).medical_history     ?? null,
        allergies:          (data as any).allergies           ?? null,
        currentMedications: (data as any).current_medications ?? null,
        surgeryHistory:     (data as any).surgery_history     ?? null,
        familyHistory:      (data as any).family_history      ?? null,
        accidentRecord:     (data as any).accident_record     ?? null,
        bloodType:          (data as any).blood_type          ?? null,
        heightCm:           (data as any).height_cm           ?? null,
        weightKg:           (data as any).weight_kg           ?? null,
      }
    } catch {
      return null
    }
  },

  async checkProviderChatExists(providerId: string): Promise<boolean> {
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return false

      const { data: patient } = await supabase
        .from('patients')
        .select('id')
        .eq('auth_id', user.id)
        .single()
      if (!patient) return false

      const { data, error } = await (supabase.rpc as any)(
        'check_patient_provider_chat_exists',
        { p_patient_id: patient.id, p_provider_id: providerId }
      )
      if (error) return false
      return Boolean(data)
    } catch {
      return false
    }
  },

  async getVisitNote(appointmentId: string): Promise<VisitNote | null> {
    try {
      const { data, error } = await supabase
        .from('visit_notes')
        .select('id, appointment_id, note_text, provider_signature, created_at')
        .eq('appointment_id', appointmentId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      if (!data) return null

      return {
        id: (data as any).id,
        appointmentId: (data as any).appointment_id,
        noteText: (data as any).note_text ?? '',
        providerSignature: (data as any).provider_signature ?? null,
        createdAt: (data as any).created_at ?? null,
      }
    } catch {
      return null
    }
  },

  async upsertVisitNote(data: UpsertVisitNoteData): Promise<{ success: boolean; note?: VisitNote; error?: string }> {
    try {
      const payload = {
        appointment_id: data.appointmentId,
        provider_id: data.providerId,
        patient_id: data.patientId,
        note_text: data.noteText.trim(),
        provider_signature: data.providerSignature.trim(),
      }

      const { data: saved, error } = await supabase
        .from('visit_notes')
        .upsert(payload, { onConflict: 'appointment_id' })
        .select('id, appointment_id, note_text, provider_signature, created_at')
        .single()

      if (error) throw error

      return {
        success: true,
        note: {
          id: (saved as any).id,
          appointmentId: (saved as any).appointment_id,
          noteText: (saved as any).note_text ?? '',
          providerSignature: (saved as any).provider_signature ?? null,
          createdAt: (saved as any).created_at ?? null,
        },
      }
    } catch (err: unknown) {
      return { success: false, error: (err as Error).message }
    }
  },

  async cancelAppointment(id: string): Promise<{ success: boolean; error?: string }> {
    try {
      const { error } = await supabase
        .from('appointments')
        .update({ status: 'cancelled' })
        .eq('id', id)
      if (error) throw error
      return { success: true }
    } catch (err: unknown) {
      return { success: false, error: (err as Error).message }
    }
  },

  /** Cancel with RPC refund — mirrors mobile cancel_appointment RPC */
  async cancelAppointmentWithRefund(
    id: string,
    reason?: string
  ): Promise<{ success: boolean; refundAmount?: number; error?: string }> {
    try {
      const { data, error } = await (supabase.rpc as any)('cancel_appointment', {
        appointment_id_param: id,
        cancellation_reason_param: reason || null,
      })
      if (error) throw error
      if (data && typeof data === 'object' && data.success) {
        analyticsService.capture('cancellation_completed', {
          source: 'appointment_service',
          refund_bucket: getAmountBucket(data.refundAmount || 0),
          has_refund: (data.refundAmount || 0) > 0,
          platform: 'web',
        })
        return { success: true, refundAmount: data.refundAmount ?? 0 }
      }
      if (data && typeof data === 'object' && data.success === false) {
        analyticsService.capture('cancellation_failed', {
          source: 'appointment_service',
          error_type: 'rpc',
          has_reason: !!reason,
          platform: 'web',
        })
        return { success: false, error: data.error ?? 'Failed to cancel appointment.' }
      }
      // Fallback: plain status update
      const { error: e2 } = await supabase
        .from('appointments')
        .update({ status: 'cancelled' })
        .eq('id', id)
      if (e2) throw e2
      analyticsService.capture('cancellation_completed', {
        source: 'appointment_service',
        refund_bucket: getAmountBucket(0),
        has_refund: false,
        platform: 'web',
      })
      return { success: true, refundAmount: 0 }
    } catch (err: unknown) {
      analyticsService.capture('cancellation_failed', {
        source: 'appointment_service',
        error_type: 'exception',
        has_reason: !!reason,
        platform: 'web',
      })
      return { success: false, error: (err as Error).message }
    }
  },

  async updateAppointmentStatus(
    id: string,
    status: 'requested' | 'scheduled' | 'completed' | 'cancelled'
  ): Promise<{ success: boolean; error?: string }> {
    // The DB check constraint requires Title Case: 'Requested' | 'Scheduled' | 'Completed' | 'Cancelled'
    const statusMap: Record<typeof status, string> = {
      requested: 'Requested',
      scheduled: 'Scheduled',
      completed: 'Completed',
      cancelled: 'Cancelled',
    }
    const dbStatus = statusMap[status]

    try {
      if (status === 'completed') {
        const { data, error } = await (supabase.rpc as any)('complete_appointment', {
          appointment_id_param: id,
        })
        if (error) throw error

        if (data && typeof data === 'object' && data.success === false) {
          return { success: false, error: data.error ?? 'Failed to complete appointment.' }
        }

        return { success: true }
      }

      const { error } = await supabase
        .from('appointments')
        .update({
          status: dbStatus,
        })
        .eq('id', id)
      if (error) throw error
      return { success: true }
    } catch (err: unknown) {
      return { success: false, error: (err as Error).message }
    }
  },

  async createAppointment(data: CreateAppointmentData): Promise<{
    success: boolean
    appointment?: Appointment
    error?: string
  }> {
    try {
      const isChatConsultation =
        data.appointmentType === 'chat' || data.serviceType === 'Chat Consultation'

      if (!isChatConsultation) {
        if (!data.medicalInfo?.currentSymptoms?.trim()) {
          analyticsService.capture('booking_failed', {
            source: 'appointment_service',
            error_type: 'validation',
            field: 'current_symptoms',
            service_type: data.serviceType,
            appointment_type: data.appointmentType || 'home',
            platform: 'web',
          })
          return { success: false, error: 'Current symptoms are required.' }
        }
        if (!data.medicalInfo?.medicalHistory?.trim()) {
          analyticsService.capture('booking_failed', {
            source: 'appointment_service',
            error_type: 'validation',
            field: 'medical_history',
            service_type: data.serviceType,
            appointment_type: data.appointmentType || 'home',
            platform: 'web',
          })
          return { success: false, error: 'Medical history is required.' }
        }
        if (!data.medicalInfo?.allergies?.trim()) {
          analyticsService.capture('booking_failed', {
            source: 'appointment_service',
            error_type: 'validation',
            field: 'allergies',
            service_type: data.serviceType,
            appointment_type: data.appointmentType || 'home',
            platform: 'web',
          })
          return { success: false, error: 'Allergy information is required.' }
        }
        if (!data.medicalInfo?.currentMedications?.trim()) {
          analyticsService.capture('booking_failed', {
            source: 'appointment_service',
            error_type: 'validation',
            field: 'current_medications',
            service_type: data.serviceType,
            appointment_type: data.appointmentType || 'home',
            platform: 'web',
          })
          return { success: false, error: 'Current medications are required.' }
        }
      }

      const { data: { user } } = await supabase.auth.getUser()
      if (!user) {
        analyticsService.capture('booking_failed', {
          source: 'appointment_service',
          error_type: 'auth',
          service_type: data.serviceType,
          appointment_type: data.appointmentType || 'home',
          platform: 'web',
        })
        return { success: false, error: 'Not authenticated.' }
      }

      const { data: patient } = await supabase
        .from('patients')
        .select('id')
        .eq('auth_id', user.id)
        .single()
      if (!patient) return { success: false, error: 'Patient profile not found.' }

      const effectiveVerificationMode =
        data.verificationMode || data.locationVerificationMode || 'device_pair'
      const effectiveDestinationLatitude =
        data.destinationLatitude ?? data.serviceAddressLatitude ?? null
      const effectiveDestinationLongitude =
        data.destinationLongitude ?? data.serviceAddressLongitude ?? null
      const effectiveDestinationAddressSnapshot =
        data.destinationAddressSnapshot ??
        (data.appointmentType === 'home' ? data.location : null)

      // Use atomic RPC — wallet deduction + appointment creation in one transaction
      const { data: rpcResult, error: rpcError } = await (supabase.rpc as any)(
        'create_appointment_and_deduct_wallet',
        {
          p_provider_id: data.providerId,
          p_patient_id: patient.id,
          p_service_type: data.serviceType,
          p_appointment_type: data.appointmentType || 'home',
          p_duration: data.duration,
          p_scheduled_date: data.scheduledDate,
          p_scheduled_time: data.scheduledTime || null,
          p_location: data.location,
          p_notes: data.notes || null,
          p_urgency_level: data.urgencyLevel || 'normal',
          p_number_of_days: data.numberOfDays || null,
          p_number_of_patients: data.numberOfPatients || null,
          p_total_cost: data.totalCost,
          p_continuation_of_appointment_id: data.continuationOfAppointmentId || null,
          p_service_address_id: data.serviceAddressId || null,
          p_verification_mode: effectiveVerificationMode,
          p_destination_latitude: effectiveDestinationLatitude,
          p_destination_longitude: effectiveDestinationLongitude,
          p_destination_address_snapshot: effectiveDestinationAddressSnapshot,
          p_current_symptoms:    data.medicalInfo?.currentSymptoms    || null,
          p_medical_history:     data.medicalInfo?.medicalHistory     || null,
          p_allergies:           data.medicalInfo?.allergies           || null,
          p_current_medications: data.medicalInfo?.currentMedications || null,
          p_surgery_history:     data.medicalInfo?.surgeryHistory     || null,
          p_family_history:      data.medicalInfo?.familyHistory      || null,
          p_accident_record:     data.medicalInfo?.accidentRecord     || null,
          p_blood_type:          data.medicalInfo?.bloodType          || null,
          p_height_cm:           data.medicalInfo?.heightCm           ?? null,
          p_weight_kg:           data.medicalInfo?.weightKg           ?? null,
        }
      )

      if (rpcError) {
        analyticsService.capture('booking_failed', {
          source: 'appointment_service',
          error_type: 'rpc',
          service_type: data.serviceType,
          appointment_type: data.appointmentType || 'home',
          amount_bucket: getAmountBucket(data.totalCost),
          platform: 'web',
        })
        return { success: false, error: rpcError.message }
      }

      const appointmentId =
        rpcResult?.appointment_id || rpcResult?.id || rpcResult

      if (!appointmentId) {
        analyticsService.capture('booking_failed', {
          source: 'appointment_service',
          error_type: 'unexpected_response',
          service_type: data.serviceType,
          appointment_type: data.appointmentType || 'home',
          amount_bucket: getAmountBucket(data.totalCost),
          platform: 'web',
        })
        return { success: false, error: 'Booking failed. Please try again.' }
      }

      const { data: created } = await supabase
        .from('appointments')
        .select('*')
        .eq('id', appointmentId)
        .single()

      analyticsService.capture('booking_completed', {
        source: 'appointment_service',
        service_type: data.serviceType,
        appointment_type: data.appointmentType || 'home',
        amount_bucket: getAmountBucket(data.totalCost),
        platform: 'web',
      })

      return { success: true, appointment: created ?? undefined }
    } catch (err: unknown) {
      analyticsService.capture('booking_failed', {
        source: 'appointment_service',
        error_type: 'exception',
        service_type: data.serviceType,
        appointment_type: data.appointmentType || 'home',
        amount_bucket: getAmountBucket(data.totalCost),
        platform: 'web',
      })
      return { success: false, error: (err as Error).message }
    }
  },
}
